import { validateEmailInput, showFieldError } from '@theme/utilities';

class EmailAndSmsSignUpForm extends HTMLElement {
  /** Flag to prevent duplicate submissions */
  isSubmitting = false;

  connectedCallback() {
    this.initValidation();
    this.initSubmitHandlers();
  }

  /** @param {string} [raw] */
  normalizePhone(raw) {
    const digits = (raw || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 10) return `+1${digits}`; // NA 10 digits → +1 prefix
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (digits.length >= 11 && digits.length <= 15) return `+${digits}`;
    return raw; // Return original if uncertain
  }

  /** Initialize field validation for email and phone inputs */
  initValidation() {
    const form = this.querySelector('.js-email-and-sms-form');
    if (!form) return;

    const emailInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="email"]'));
    const phoneInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="tel"]'));

    // Email field events
    if (emailInput) {
      emailInput.addEventListener('blur', () => {
        this.validateEmail(emailInput, false);
      });
      emailInput.addEventListener('input', () => {
        const email = emailInput.value.trim();
        const errorElement = /** @type {HTMLElement | null} */ (
          this.querySelector('.js-email-and-sms-form-email-error')
        );

        if (!email) {
          showFieldError(emailInput, errorElement, false);
        } else if (emailInput.classList.contains('error')) {
          this.validateEmail(emailInput, false);
        }
      });
    }

    // Phone field events
    if (phoneInput) {
      phoneInput.addEventListener('blur', () => {
        this.validatePhone(phoneInput, false);
      });
      phoneInput.addEventListener('input', () => {
        const phone = phoneInput.value.trim();
        const errorElement = /** @type {HTMLElement | null} */ (
          this.querySelector('.js-email-and-sms-form-phone-error')
        );

        if (!phone) {
          showFieldError(phoneInput, errorElement, false);
        } else if (phoneInput.classList.contains('error')) {
          this.validatePhone(phoneInput, false);
        }
      });
    }
  }

  validateEmail(/** @type {HTMLInputElement} */ emailInput, /** @type {boolean} */ requireErrorOnEmpty = true) {
    const errorElement = /** @type {HTMLElement | null} */ (this.querySelector('.js-email-and-sms-form-email-error'));
    return validateEmailInput(emailInput, errorElement, requireErrorOnEmpty);
  }

  /**
   * Validate phone input.
   * Optional field — returns true when empty.
   */
  validatePhone(/** @type {HTMLInputElement} */ phoneInput, /** @type {boolean} */ requireErrorOnEmpty = true) {
    const phone = phoneInput.value.trim();
    const errorElement = /** @type {HTMLElement | null} */ (this.querySelector('.js-email-and-sms-form-phone-error'));

    if (!phone) {
      showFieldError(phoneInput, errorElement, false);
      return true; // Optional field
    }

    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(phone) || phone.replace(/\D/g, '').length < 10) {
      showFieldError(phoneInput, errorElement, true);
      return false;
    }

    showFieldError(phoneInput, errorElement, false);
    return true;
  }

  /**
   * Validate the entire form.
   * Returns true if both email and phone are valid.
   */
  validateForm(/** @type {HTMLFormElement} */ form) {
    const emailInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="email"]'));
    const phoneInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="tel"]'));

    const isEmailValid = emailInput ? this.validateEmail(emailInput, true) : false;
    const isPhoneValid = phoneInput ? this.validatePhone(phoneInput, true) : true;

    return isEmailValid && isPhoneValid;
  }

  /**
   * Handle form submission, including loading state,
   * payload construction, and API request to Klaviyo.
   */
  initSubmitHandlers() {
    this.addEventListener('submit', async (e) => {
      const form = /** @type {HTMLFormElement | null} */ (e.target);
      if (!form || !form.classList.contains('js-email-and-sms-form')) return;

      e.preventDefault();

      // Prevent duplicate submissions
      if (this.isSubmitting) return;
      if (!this.validateForm(form)) return;

      const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
      const buttonText = button?.querySelector('.button-text');
      const buttonLoading = button?.querySelector('.button-loading');

      const listId = (form.dataset.listId || Theme?.settings?.klaviyo_email_subscription_list_id || '').trim();
      const companyId = (Theme?.settings?.klaviyo_company_id || '').trim();
      const data = new FormData(form);
      const email = String(data.get('email') || '')
        .trim()
        .toLowerCase();
      const rawPhone = String(data.get('phone') || '');
      const phone = rawPhone ? this.normalizePhone(rawPhone) : '';
      const formSource = String(data.get('custom_source') || '');

      if (!companyId) {
        console.error('Klaviyo Company ID is required but not provided');
        return;
      }

      // Show loading state
      this.isSubmitting = true;
      if (buttonText) buttonText.classList.add('hidden');
      if (buttonLoading) buttonLoading.classList.remove('hidden');
      if (button) button.disabled = true;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  ...(phone ? { phone_number: phone } : {}),
                  subscriptions: {
                    email: { marketing: { consent: 'SUBSCRIBED' } },
                    ...(phone && { sms: { marketing: { consent: 'SUBSCRIBED' } } }),
                  },
                },
              },
            },
            ...(formSource ? { custom_source: formSource } : {}),
          },
          ...(listId && {
            relationships: {
              list: { data: { type: 'list', id: listId } },
            },
          }),
        },
      };

      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: '2025-10-15',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          form.reset();
          this.showSuccess();
        } else {
          let msg = 'Subscription failed';

          try {
            const errorData = await response.json();
            if (errorData?.errors?.length) {
              const detail = errorData.errors[0].detail || errorData.errors[0].title || msg;
              msg =
                detail.toLowerCase().includes('list') && detail.toLowerCase().includes('not found')
                  ? 'List not found. Check Email Subscription List ID in theme settings (Klaviyo: Audience → Lists → your list → ID in URL).'
                  : detail;
            } else {
              console.error('Unexpected error format:', errorData);
            }
          } catch (parseErr) {
            console.error('Error parsing JSON response:', parseErr);
          }

          this.showError(msg);
        }
      } catch (err) {
        console.error('Error during subscription:', err);
        this.showError('Network error. Please try again.');
      } finally {
        this.isSubmitting = false;
        if (buttonText) buttonText.classList.remove('hidden');
        if (buttonLoading) buttonLoading.classList.add('hidden');
        if (button) button.disabled = false;
      }
    });
  }

  /** Show success message and hide form content */
  showSuccess() {
    const formContent = this.querySelector('.js-email-and-sms-form-content');
    const successContainer = this.querySelector('.js-email-and-sms-form-success');

    if (formContent && successContainer) {
      formContent.classList.add('hidden');
      successContainer.classList.remove('hidden');
    }
  }

  /** Show error message and reset submit button state */
  showError(message = 'Failed to subscribe to newsletter') {
    console.error(message);

    const form = this.querySelector('.js-email-and-sms-form');
    if (form) {
      const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
      const buttonText = button?.querySelector('.button-text');
      const buttonLoading = button?.querySelector('.button-loading');

      if (buttonText) buttonText.classList.remove('hidden');
      if (buttonLoading) buttonLoading.classList.add('hidden');
      if (button) button.disabled = false;
    }
  }
}

const tagName = 'signup-drawer-form-component';
if (!customElements.get(tagName)) {
  customElements.define(tagName, EmailAndSmsSignUpForm);
}
